import { NeonColors, FONTS, neonButton, neonText, SCREEN_BASE_STYLE } from '../neon-theme'
import { mountToAppShell } from '../appShell'

export interface TutorialSlide {
  mount(container: HTMLDivElement, onReady: () => void): void
  unmount(): void
}

const SLIDE_TRANSITION_MS = 400
const AUTO_ADVANCE_PAUSE_MS = 800

export class TutorialScreen {
  private container: HTMLDivElement
  private slidesWrapper: HTMLDivElement | null = null
  private dotsContainer: HTMLDivElement | null = null
  private bottomBar: HTMLDivElement | null = null
  private progressBar: HTMLDivElement | null = null
  private continueBtn: HTMLButtonElement | null = null

  private slides: TutorialSlide[] = []
  private slideElements: HTMLDivElement[] = []
  private currentIndex = 0
  private isTransitioning = false
  private autoTimer: ReturnType<typeof setTimeout> | null = null
  private slideDurations: number[] = []
  private loadingComplete = false
  private onContinue: (() => void) | null = null

  private touchStartX = 0
  private touchStartY = 0
  private isSwiping = false

  constructor() {
    this.container = document.createElement('div')
    this.container.id = 'tutorial-screen'
  }

  show(slides: TutorialSlide[], durations: number[], onContinue: () => void): void {
    this.slides = slides
    this.slideDurations = durations
    this.onContinue = onContinue
    this.currentIndex = 0
    this.loadingComplete = false

    this.container.style.cssText = `
      ${SCREEN_BASE_STYLE}
      background: ${NeonColors.bgDark};
      z-index: 250;
      opacity: 1;
      user-select: none;
      -webkit-user-select: none;
      touch-action: pan-y;
    `

    this.container.innerHTML = ''
    this.buildDOM()
    mountToAppShell(this.container)
    this.mountSlide(0)
    this.scheduleAutoAdvance()
    this.bindTouchEvents()
  }

  setLoadingProgress(progress: number): void {
    if (this.progressBar) {
      this.progressBar.style.width = `${Math.min(100, Math.max(0, progress))}%`
    }
    if (progress >= 100 && !this.loadingComplete) {
      this.loadingComplete = true
      this.showContinueButton()
    }
  }

  hide(): void {
    this.clearAutoTimer()
    this.container.style.opacity = '0'
    this.container.style.transition = `opacity 400ms ease-out`
    setTimeout(() => {
      this.unmountAllSlides()
      this.container.remove()
    }, 400)
  }

  dispose(): void {
    this.clearAutoTimer()
    this.unmountAllSlides()
    this.container.remove()
    this.onContinue = null
  }

  private buildDOM(): void {
    this.slidesWrapper = document.createElement('div')
    this.slidesWrapper.style.cssText = `
      position: absolute; inset: 0;
      overflow: hidden;
    `
    this.container.appendChild(this.slidesWrapper)

    this.dotsContainer = document.createElement('div')
    this.dotsContainer.style.cssText = `
      display: flex;
      gap: 10px;
      justify-content: center;
      margin-bottom: 12px;
    `
    for (let i = 0; i < this.slides.length; i++) {
      const dot = document.createElement('div')
      dot.dataset.index = String(i)
      dot.style.cssText = `
        width: 8px; height: 8px;
        border-radius: 50%;
        background: ${i === 0 ? NeonColors.primary : NeonColors.textDim};
        transition: background 0.3s, transform 0.3s, box-shadow 0.3s;
        ${i === 0 ? `transform: scale(1.3); box-shadow: 0 0 10px ${NeonColors.primaryGlow};` : ''}
        cursor: pointer;
      `
      dot.addEventListener('click', () => this.goToSlide(i))
      this.dotsContainer.appendChild(dot)
    }
    this.bottomBar = document.createElement('div')
    this.bottomBar.style.cssText = `
      position: absolute;
      bottom: calc(40px + 5%);
      left: 50%;
      transform: translateX(-50%);
      z-index: 10;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 6px;
    `

    this.bottomBar.appendChild(this.dotsContainer)

    const progressTrack = document.createElement('div')
    progressTrack.style.cssText = `
      width: 200px; height: 4px;
      border-radius: 2px;
      background: rgba(255,255,255,0.12);
      overflow: hidden;
    `
    this.progressBar = document.createElement('div')
    this.progressBar.style.cssText = `
      width: 0%; height: 100%;
      border-radius: 2px;
      background: ${NeonColors.primary};
      box-shadow: 0 0 6px ${NeonColors.primaryGlow};
      transition: width 400ms ease-out;
    `
    progressTrack.appendChild(this.progressBar)
    this.bottomBar.appendChild(progressTrack)

    this.continueBtn = document.createElement('button')
    this.continueBtn.textContent = 'CONTINUE'
    this.continueBtn.style.cssText = `
      ${neonButton(NeonColors.primary, NeonColors.primaryGlow)}
      padding: 16px 48px;
      font-size: 18px;
      min-height: 58px;
      opacity: 0;
      transform: translateY(10px);
      transition: opacity 0.4s ease, transform 0.4s ease;
      pointer-events: none;
    `
    this.continueBtn.addEventListener('click', () => {
      this.onContinue?.()
    })
    this.bottomBar.appendChild(this.continueBtn)

    this.container.appendChild(this.bottomBar)
  }

  private showContinueButton(): void {
    if (!this.continueBtn || !this.progressBar) return
    const track = this.progressBar.parentElement
    if (track) {
      track.style.transition = 'opacity 0.3s ease, height 0.3s ease'
      track.style.opacity = '0'
      track.style.height = '0'
    }
    setTimeout(() => {
      if (!this.continueBtn) return
      this.continueBtn.style.opacity = '1'
      this.continueBtn.style.transform = 'translateY(0)'
      this.continueBtn.style.pointerEvents = 'auto'
    }, 300)
  }

  private mountSlide(index: number): void {
    if (!this.slidesWrapper) return
    const slide = this.slides[index]
    if (!slide) return

    const el = document.createElement('div')
    el.style.cssText = `
      position: absolute; inset: 0;
      display: flex; flex-direction: column;
      align-items: center; justify-content: center;
      padding: 24px;
    `
    this.slideElements[index] = el
    this.slidesWrapper.appendChild(el)
    slide.mount(el, () => {})
  }

  private unmountSlide(index: number): void {
    const slide = this.slides[index]
    slide?.unmount()
    const el = this.slideElements[index]
    el?.remove()
    delete this.slideElements[index]
  }

  private unmountAllSlides(): void {
    for (let i = 0; i < this.slides.length; i++) {
      this.unmountSlide(i)
    }
  }

  private goToSlide(targetIndex: number): void {
    if (this.isTransitioning) return
    if (targetIndex === this.currentIndex) return
    if (targetIndex < 0 || targetIndex >= this.slides.length) return

    this.clearAutoTimer()
    this.isTransitioning = true

    const direction = targetIndex > this.currentIndex ? 1 : -1
    const oldIndex = this.currentIndex
    const oldEl = this.slideElements[oldIndex]

    this.mountSlide(targetIndex)
    const newEl = this.slideElements[targetIndex]

    if (oldEl && newEl) {
      newEl.style.transform = `translateX(${direction * 100}%)`
      newEl.style.transition = 'none'

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const t = `transform ${SLIDE_TRANSITION_MS}ms ease-in-out`
          oldEl.style.transition = t
          newEl.style.transition = t
          oldEl.style.transform = `translateX(${-direction * 100}%)`
          newEl.style.transform = 'translateX(0)'
        })
      })

      setTimeout(() => {
        this.unmountSlide(oldIndex)
        this.currentIndex = targetIndex
        this.updateDots()
        this.isTransitioning = false
        this.scheduleAutoAdvance()
      }, SLIDE_TRANSITION_MS)
    } else {
      this.currentIndex = targetIndex
      this.updateDots()
      this.isTransitioning = false
      this.scheduleAutoAdvance()
    }
  }

  private nextSlide(): void {
    const next = (this.currentIndex + 1) % this.slides.length
    this.goToSlide(next)
  }

  private prevSlide(): void {
    const prev = (this.currentIndex - 1 + this.slides.length) % this.slides.length
    this.goToSlide(prev)
  }

  private updateDots(): void {
    if (!this.dotsContainer) return
    const dots = this.dotsContainer.children
    for (let i = 0; i < dots.length; i++) {
      const dot = dots[i] as HTMLElement
      const isActive = i === this.currentIndex
      dot.style.background = isActive ? NeonColors.primary : NeonColors.textDim
      dot.style.transform = isActive ? 'scale(1.3)' : 'scale(1)'
      dot.style.boxShadow = isActive ? `0 0 10px ${NeonColors.primaryGlow}` : 'none'
    }
  }

  private scheduleAutoAdvance(): void {
    // Auto-advance disabled — user swipes manually
  }

  private clearAutoTimer(): void {
    if (this.autoTimer != null) {
      clearTimeout(this.autoTimer)
      this.autoTimer = null
    }
  }

  private bindTouchEvents(): void {
    // Touch swipe
    this.container.addEventListener('touchstart', (e) => {
      if (e.touches.length !== 1) return
      this.touchStartX = e.touches[0]!.clientX
      this.touchStartY = e.touches[0]!.clientY
      this.isSwiping = false
    }, { passive: true })

    this.container.addEventListener('touchmove', (e) => {
      if (e.touches.length !== 1) return
      const dx = e.touches[0]!.clientX - this.touchStartX
      const dy = e.touches[0]!.clientY - this.touchStartY
      if (Math.abs(dx) > Math.abs(dy) * 1.5 && Math.abs(dx) > 15) {
        this.isSwiping = true
      }
    }, { passive: true })

    this.container.addEventListener('touchend', (e) => {
      if (!this.isSwiping) return
      const dx = (e.changedTouches[0]?.clientX ?? this.touchStartX) - this.touchStartX
      if (Math.abs(dx) > 30) {
        if (dx < 0) this.nextSlide()
        else this.prevSlide()
      }
      this.isSwiping = false
    }, { passive: true })

    // Mouse drag (desktop)
    let mouseDown = false
    let mouseStartX = 0

    this.container.addEventListener('mousedown', (e) => {
      const target = e.target as HTMLElement
      if (target.tagName === 'BUTTON' || target.closest('button')) return
      if (target.dataset.index != null) return
      mouseDown = true
      mouseStartX = e.clientX
    })

    this.container.addEventListener('mouseup', (e) => {
      if (!mouseDown) return
      mouseDown = false
      const dx = e.clientX - mouseStartX
      if (Math.abs(dx) > 40) {
        if (dx < 0) this.nextSlide()
        else this.prevSlide()
      }
    })

    this.container.addEventListener('mouseleave', () => {
      mouseDown = false
    })

  }
}
